import {
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
  type HubConnection,
} from '@microsoft/signalr'
import { useEffect, useRef, useState } from 'react'

import { getAccessToken, refreshAccessToken } from '@/api/authTokenStore'

/**
 * Én forbindelse pr. fane, delt af alt hvad der lytter med.
 *
 * SignalR åbner en websocket. Havde hver komponent sin egen, ville en
 * projektside med en tegning og et kommentarpanel holde flere sockets åbne mod
 * den samme server — og en byggeplads med dårlig dækning betaler for hver af
 * dem. Forbindelsen tælles derfor op og ned og lukkes, når den sidste lytter
 * forsvinder.
 */
let sharedConnection: HubConnection | null = null
let listenerCount = 0

/**
 * Den igangværende opstart. Uden den ville to komponenter, der monteres i samme
 * render-pass, hver kalde `start()`, og den ene fejle med "cannot start a
 * connection that is not in the Disconnected state".
 */
let startPromise: Promise<void> | null = null

/**
 * Nedlukningen udskydes et øjeblik.
 *
 * React monterer effekter to gange i udvikling, og en forbindelse, der lukkes
 * midt i sin egen forhandling, fejler med "The connection was stopped during
 * negotiation" frem for at komme op. Den samme situation opstår i produktion,
 * når brugeren skifter mellem to faner, der begge lytter med: uden pausen ville
 * socket'en blive lukket og åbnet igen for ingenting.
 */
let stopTimer: ReturnType<typeof setTimeout> | null = null

const STOP_DELAY_MS = 1000

/**
 * Livscyklus-lyttere, mounts kan til- og afmelde sig.
 *
 * `onreconnected` og dens søskende kan ikke afmeldes igen — @microsoft/signalr
 * samler dem i private lister uden nogen `off`-pendant. Registrerede hvert
 * mount sin egen callback direkte på forbindelsen, ville listen vokse for hver
 * side, brugeren har besøgt, og gamle callbacks blive kaldt for evigt. Derfor
 * registreres én stabil callback ved forbindelsens oprettelse, som dispatcher
 * til de her Sets.
 */
const reconnectedListeners = new Set<() => void>()
const connectionLostListeners = new Set<() => void>()

function acquireConnection(): HubConnection {
  if (stopTimer !== null) {
    clearTimeout(stopTimer)
    stopTimer = null
  }

  if (sharedConnection === null) {
    const connection = new HubConnectionBuilder()
      .withUrl('/hubs/project', {
        // Websockets kan ikke sætte en Authorization-header, så tokenet går i
        // query-strengen. Funktionen kaldes ved hver genforbindelse, så et
        // fornyet token bliver brugt frem for det, der var gyldigt ved
        // sideindlæsningen.
        accessTokenFactory: async () => getAccessToken() ?? (await refreshAccessToken()) ?? '',
      })
      // Standardplanen giver op efter fire forsøg på under et minut. På en
      // byggeplads er et minut uden dækning ikke et nedbrud, det er en tur ned i
      // kælderen — så vi bliver ved med at prøve med aftagende hyppighed.
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000, 60000])
      .configureLogging(LogLevel.Warning)
      .build()

    connection.onreconnected(() => {
      for (const listener of reconnectedListeners) listener()
    })
    connection.onreconnecting(() => {
      for (const listener of connectionLostListeners) listener()
    })
    connection.onclose(() => {
      for (const listener of connectionLostListeners) listener()
    })

    sharedConnection = connection
  }

  listenerCount += 1

  return sharedConnection
}

function releaseConnection(connection: HubConnection): void {
  listenerCount -= 1

  if (listenerCount > 0) {
    return
  }

  stopTimer = setTimeout(() => {
    stopTimer = null

    // Nogen nåede at melde sig til igen imens. Så bliver forbindelsen stående.
    if (listenerCount > 0 || sharedConnection !== connection) {
      return
    }

    sharedConnection = null
    startPromise = null

    void connection.stop().catch(() => undefined)
  }, STOP_DELAY_MS)
}

/** Starter forbindelsen, hvis den ikke allerede er i gang. */
async function ensureStarted(connection: HubConnection): Promise<void> {
  if (connection.state === HubConnectionState.Connected) {
    return
  }

  if (connection.state === HubConnectionState.Disconnected) {
    startPromise ??= connection.start().finally(() => {
      startPromise = null
    })
  }

  await startPromise
}

/**
 * Lytter med på et projekt.
 *
 * Beskederne er en bekvemmelighed, ikke en garanti: mister forbindelsen et
 * øjeblik, får klienten ikke det, den gik glip af, leveret bagefter. Derfor
 * melder hooket tilbage, når den er genoprettet, så kalderen kan hente listen
 * igen — det er billigere og mere rigtigt end at forsøge at gætte, hvad der
 * skete undervejs.
 */
export function useProjectHub(
  projectId: string | null,
  handlers: Record<string, (payload: never) => void>,
  options: { onReconnected?: () => void } = {},
): { isConnected: boolean } {
  const [isConnected, setIsConnected] = useState(false)

  // Handlers ændrer sig ved hver render. Vi holder dem i en ref, så effekten
  // ikke skal genkøre — og forbindelsen ikke rives ned — hver gang en
  // forældrekomponent tegner igen.
  const handlerRef = useRef(handlers)
  handlerRef.current = handlers

  const reconnectedRef = useRef(options.onReconnected)
  reconnectedRef.current = options.onReconnected

  useEffect(() => {
    if (!projectId) {
      return
    }

    const connection = acquireConnection()
    const eventNames = Object.keys(handlerRef.current)

    // Hver lytter får sin egen indpakning, der slår op i ref'en. Uden det
    // ville afmeldingen skulle kende præcis den funktion, der blev tilmeldt.
    const bound = eventNames.map((event) => {
      const listener = (...args: unknown[]) => {
        handlerRef.current[event]?.(args[0] as never)
      }

      connection.on(event, listener)
      return { event, listener }
    })

    let cancelled = false

    const handleReconnected = () => {
      if (cancelled) return

      setIsConnected(true)
      void connection.invoke('JoinProject', projectId).catch(() => undefined)
      reconnectedRef.current?.()
    }

    const handleLost = () => {
      if (!cancelled) setIsConnected(false)
    }

    reconnectedListeners.add(handleReconnected)
    connectionLostListeners.add(handleLost)

    ensureStarted(connection)
      .then(async () => {
        if (cancelled) return

        await connection.invoke('JoinProject', projectId)

        if (!cancelled) setIsConnected(true)
      })
      .catch(() => {
        // En manglende realtidsforbindelse er ikke en fejl, brugeren skal
        // forstyrres med. Fladen virker uden — den opdaterer bare ikke af sig
        // selv, og indikatoren i UI'et siger det.
        if (!cancelled) setIsConnected(false)
      })

    return () => {
      cancelled = true

      reconnectedListeners.delete(handleReconnected)
      connectionLostListeners.delete(handleLost)

      for (const { event, listener } of bound) {
        connection.off(event, listener)
      }

      // Gruppen forlades også som sidste lytter. Den udskudte nedlukning kan
      // blive aflyst af en ny `acquireConnection`, og så genbruges
      // forbindelsen — med det gamle projekts gruppemedlemskab, hvis ingen
      // meldte fra. Lukkes den alligevel, koster den ekstra LeaveProject
      // intet: serveren rydder grupperne ved frakoblingen.
      if (connection.state === HubConnectionState.Connected) {
        void connection.invoke('LeaveProject', projectId).catch(() => undefined)
      }

      releaseConnection(connection)
    }
    // Kun projektet: handlers ligger i en ref netop for ikke at stå her.
  }, [projectId])

  return { isConnected }
}

/**
 * Lytter efter beskeder til brugeren selv.
 *
 * Der meldes ikke til nogen gruppe herfra: serveren melder forbindelsen til
 * `user:{id}` ud fra tokenet. Et gruppenavn, klienten selv kunne sende, ville
 * betyde, at et gæt på et bruger-id gav besked, hver gang en anden fik post.
 *
 * Beskeden bærer intet indhold — kun "hent igen". Kaldet henter selv listen
 * med sin egen adgang, og en besked kan derfor hverken lække noget eller blive
 * forældet.
 */
export function useNotificationHub(onChanged: () => void): void {
  const changedRef = useRef(onChanged)
  changedRef.current = onChanged

  useEffect(() => {
    // Klokken sidder i topbaren og vises på hver eneste side. Kan der ikke
    // rejses en forbindelse — en adresse, SignalR ikke kan opløse, en browser
    // uden websockets — må det ikke rive fladen med. Indbakken opdaterer så
    // kun på sit interval, og det er hele forskellen.
    let connection: HubConnection

    try {
      connection = acquireConnection()
    } catch {
      return
    }

    const listener = () => changedRef.current()
    connection.on('notificationsChanged', listener)

    // En genforbindelse betyder, at vi kan være gået glip af noget imens.
    const handleReconnected = () => changedRef.current()
    reconnectedListeners.add(handleReconnected)

    void ensureStarted(connection).catch(() => undefined)

    return () => {
      reconnectedListeners.delete(handleReconnected)
      connection.off('notificationsChanged', listener)
      releaseConnection(connection)
    }
  }, [])
}

/**
 * Melder til og fra et enkelt tegningsark.
 *
 * Markeringer sendes kun til arkets egen gruppe. Projektets øvrige medlemmer
 * skal ikke have en besked, hver gang nogen tegner en pil.
 *
 * Kræver, at <see cref="useProjectHub"/> allerede har rejst forbindelsen —
 * arket ligger inde i et projekt, som fladen i forvejen lytter med på.
 */
export function useSheetSubscription(projectId: string | null, sheetId: string | null): void {
  useEffect(() => {
    const connection = sharedConnection

    if (!projectId || !sheetId || !connection) {
      return
    }

    let cancelled = false

    ensureStarted(connection)
      .then(async () => {
        if (cancelled || connection.state !== HubConnectionState.Connected) return

        await connection.invoke('JoinSheet', projectId, sheetId)
      })
      .catch(() => undefined)

    return () => {
      cancelled = true

      if (connection.state === HubConnectionState.Connected) {
        void connection.invoke('LeaveSheet', sheetId).catch(() => undefined)
      }
    }
  }, [projectId, sheetId])
}
